import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed, batch } from '@preact/signals'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import {
    calcInventoryReconciliation,
    type RefrigerantRow,
    type RiskLevel,
} from '../../util/inventory-reconciliation.js'
import { exportPdf, type PdfSection } from '../../util/pdf.js'
import { EM_DASH } from '../../constants.js'
import './inventory-reconciliation-calculator.css'

const RISK_LABELS:Record<RiskLevel, string> = {
    low: 'Low Risk ' + EM_DASH + ' reconciliation looks balanced.',
    moderate: 'Moderate Risk ' + EM_DASH + ' some unaccounted usage detected.',
    high: 'High Risk ' + EM_DASH + ' significant unaccounted refrigerant.',
    critical: 'Critical ' + EM_DASH + ' recordkeeping gap(s) detected.',
}

function makeEmptyRow ():RefrigerantRow & { id:number } {
    return {
        id: nextId(),
        refrigerantName: '',
        purchased: 0,
        recovered: 0,
        currentInventory: 0,
    }
}

let _idCounter = 0
function nextId ():number {
    _idCounter += 1
    return _idCounter
}

const companyName = signal<string>('')
const rows = signal<Array<RefrigerantRow & { id:number }>>([makeEmptyRow()])
const submitted = signal(false)

const result = computed(() => {
    if (!submitted.value) return null
    const validRows = rows.value.filter(r => r.refrigerantName.trim() !== '')
    if (validRows.length === 0) return null
    return calcInventoryReconciliation(validRows)
})

function handleRowChange (
    id:number,
    field:keyof RefrigerantRow,
    value:string
):void {
    rows.value = rows.value.map(row => {
        if (row.id !== id) return row
        if (field === 'refrigerantName') {
            return { ...row, refrigerantName: value }
        }
        return { ...row, [field]: parseFloat(value) || 0 }
    })
}

function handleAddRow ():void {
    rows.value = [...rows.value, makeEmptyRow()]
}

function handleRemoveRow (id:number):void {
    if (rows.value.length <= 1) return
    rows.value = rows.value.filter(row => row.id !== id)
}

function handleSubmit (e:Event):void {
    e.preventDefault()
    submitted.value = true
}

function handleReset (e:Event):void {
    e.preventDefault()
    batch(() => {
        companyName.value = ''
        rows.value = [makeEmptyRow()]
        submitted.value = false
    })
}

function handleDownloadPdf (e:Event):void {
    e.preventDefault()
    const r = result.value
    if (!r) return

    const sections:PdfSection[] = []

    if (companyName.value.trim()) {
        sections.push({
            heading: 'Company',
            rows: [{ label: 'Company Name', value: companyName.value }],
        })
    }

    sections.push({
        heading: 'Per-Refrigerant Summary',
        rows: r.rows.map(row => ({
            label: row.refrigerantName,
            value: `Purchased: ${row.purchased} lbs | ` +
                `Recovered: ${row.recovered} lbs | ` +
                `Inventory: ${row.currentInventory} lbs | ` +
                `Implied Usage: ${row.impliedUsage} lbs` +
                (row.hasGap ? ' [GAP]' : ''),
        })),
    })

    sections.push({
        heading: 'Totals',
        rows: [
            {
                label: 'Total Purchased (lbs)',
                value: String(r.totalPurchased),
            },
            {
                label: 'Total Recovered (lbs)',
                value: String(r.totalRecovered),
            },
            {
                label: 'Total Inventory (lbs)',
                value: String(r.totalInventory),
            },
            {
                label: 'Total Implied Usage (lbs)',
                value: String(r.totalImpliedUsage),
            },
            { label: 'Recordkeeping Gap', value: r.hasAnyGap ? 'Yes' : 'No' },
            { label: 'Risk Level', value: r.riskLevel.toUpperCase() },
        ],
    })

    if (r.recommendations.length > 0) {
        sections.push({
            heading: 'Recommendations',
            rows: r.recommendations.map((rec, i) => ({
                label: String(i + 1),
                value: rec,
            })),
        })
    }

    exportPdf({
        title: companyName.value.trim() ?
            'Refrigerant Inventory Reconciliation ' + EM_DASH + ` ${companyName.value}` :
            'Refrigerant Inventory Reconciliation',
        sections,
        footer:
            'Operational reconciliation only. Not a regulatory ' +
            'purchase-limit calculation. Not legal advice. Does not ' +
            'predict enforcement outcomes.',
    })
}

function fmtLbs (n:number):string {
    return `${n.toFixed(2)} lbs`
}

export const InventoryReconciliationCalculator:FunctionComponent<{
    state:AppState
}> = function InventoryReconciliationCalculator () {
    const r = result.value

    return html`
        <${ToolPage}
            title="Purchase Limit / Inventory Reconciliation Calculator"
            description="Reconcile refrigerant purchased, recovered, and
                on-hand inventory to surface recordkeeping gaps and assess
                compliance risk."
        >
            <${ToolWorkbench}
                emptyHint="Add your refrigerants, then select Calculate to
                    reconcile inventory."
            >
                <form class="tool-form" onSubmit=${handleSubmit} novalidate>

                <div class="tool-form-section">
                    <p class="tool-section-label">Company</p>
                    <div class="tool-grid">
                        <div class="tool-field">
                            <label class="tool-field-label"
                                for="irc-company-name">
                                Company Name (optional)
                            </label>
                            <input
                                id="irc-company-name"
                                class="tool-input"
                                type="text"
                                value=${companyName.value}
                                onInput=${(e:Event) => {
                                    companyName.value =
                                        (e.target as HTMLInputElement).value
                                }}
                                placeholder="e.g. Acme HVAC Services"
                            />
                        </div>
                    </div>
                </div>

                <div class="tool-form-section">
                    <p class="tool-section-label">Refrigerants</p>
                    ${rows.value.map(row => html`
                        <div class="irc-row" key=${row.id}>
                            <div class="tool-grid">
                                <div class="tool-field">
                                    <label
                                        class="tool-field-label"
                                        for=${`irc-refrigerant-${row.id}`}
                                    >
                                        Refrigerant Type
                                    </label>
                                    <input
                                        id=${`irc-refrigerant-${row.id}`}
                                        class="tool-input"
                                        type="text"
                                        value=${row.refrigerantName}
                                        onInput=${(e:Event) =>
                                            handleRowChange(
                                                row.id,
                                                'refrigerantName',
                                                (e.target as
                                                    HTMLInputElement).value
                                            )
                                        }
                                        placeholder="e.g. R-410A"
                                        required
                                    />
                                </div>
                                <div class="tool-field">
                                    <label
                                        class="tool-field-label"
                                        for=${`irc-purchased-${row.id}`}
                                    >
                                        Purchased (lbs)
                                    </label>
                                    <input
                                        id=${`irc-purchased-${row.id}`}
                                        class="tool-input"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value=${row.purchased}
                                        onInput=${(e:Event) =>
                                            handleRowChange(
                                                row.id,
                                                'purchased',
                                                (e.target as
                                                    HTMLInputElement).value
                                            )
                                        }
                                        required
                                    />
                                </div>
                                <div class="tool-field">
                                    <label
                                        class="tool-field-label"
                                        for=${`irc-recovered-${row.id}`}
                                    >
                                        Recovered (lbs)
                                    </label>
                                    <input
                                        id=${`irc-recovered-${row.id}`}
                                        class="tool-input"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value=${row.recovered}
                                        onInput=${(e:Event) =>
                                            handleRowChange(
                                                row.id,
                                                'recovered',
                                                (e.target as
                                                    HTMLInputElement).value
                                            )
                                        }
                                        required
                                    />
                                </div>
                                <div class="tool-field">
                                    <label
                                        class="tool-field-label"
                                        for=${`irc-inventory-${row.id}`}
                                    >
                                        Current Inventory (lbs)
                                    </label>
                                    <input
                                        id=${`irc-inventory-${row.id}`}
                                        class="tool-input"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value=${row.currentInventory}
                                        onInput=${(e:Event) =>
                                            handleRowChange(
                                                row.id,
                                                'currentInventory',
                                                (e.target as
                                                    HTMLInputElement).value
                                            )
                                        }
                                        required
                                    />
                                </div>
                            </div>
                            ${rows.value.length > 1 && html`
                                <div class="tool-actions">
                                    <substrate-button
                                        type="button"
                                        onClick=${(e:Event) => {
                                            e.preventDefault()
                                            handleRemoveRow(row.id)
                                        }}
                                    >Remove</substrate-button>
                                </div>
                            `}
                        </div>
                    `)}
                </div>

                <div class="tool-actions">
                    <substrate-button
                        type="button"
                        onClick=${(e:Event) => {
                            e.preventDefault()
                            handleAddRow()
                        }}
                    >+ Add Another Refrigerant</substrate-button>
                </div>

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Calculate
                    </substrate-button>
                </div>

            </form>

            ${r && html`
                <div class="tool-readout">
                    <div class="tool-metrics">
                        <div class="tool-metric">
                            <p class="tool-metric-label">Total Purchased</p>
                            <p class="tool-metric-value">
                                ${fmtLbs(r.totalPurchased)}
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">Total Recovered</p>
                            <p class="tool-metric-value">
                                ${fmtLbs(r.totalRecovered)}
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">On-Hand Inventory</p>
                            <p class="tool-metric-value">
                                ${fmtLbs(r.totalInventory)}
                            </p>
                        </div>
                        <div class="tool-metric">
                            <p class="tool-metric-label">Implied Usage</p>
                            <p class="tool-metric-value">
                                ${fmtLbs(r.totalImpliedUsage)}
                            </p>
                        </div>
                    </div>

                    <h3>Per-Refrigerant Breakdown</h3>
                    <div class="tool-table-wrap">
                        <table class="tool-table">
                            <thead>
                                <tr>
                                    <th>Refrigerant</th>
                                    <th>Purchased</th>
                                    <th>Recovered</th>
                                    <th>Inventory</th>
                                    <th>Implied Usage</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${r.rows.map(row => html`
                                    <tr key=${row.refrigerantName}>
                                        <td>${row.refrigerantName}</td>
                                        <td>${fmtLbs(row.purchased)}</td>
                                        <td>${fmtLbs(row.recovered)}</td>
                                        <td>${fmtLbs(row.currentInventory)}</td>
                                        <td data-alert=${
                                            row.hasGap ? 'true' : 'false'
                                        }>
                                            ${fmtLbs(row.impliedUsage)}
                                        </td>
                                        <td data-alert=${
                                            row.hasGap ? 'true' : 'false'
                                        }>
                                            ${row.hasGap ?
                                                'Recordkeeping Gap' :
                                                'OK'
                                            }
                                        </td>
                                    </tr>
                                `)}
                            </tbody>
                        </table>
                    </div>

                    <span class="tool-badge" data-level=${r.riskLevel}>
                        ${r.riskLevel} risk
                    </span>
                    <p class="tool-readout-detail">
                        ${RISK_LABELS[r.riskLevel]}
                    </p>

                    ${r.recommendations.length > 0 && html`
                        <div class="tool-recs">
                            <h3>Recommendations</h3>
                            <ul>
                                ${r.recommendations.map((rec, i) => html`
                                    <li key=${i}>${rec}</li>
                                `)}
                            </ul>
                        </div>
                    `}

                    <div class="tool-actions">
                        <substrate-button
                            type="button"
                            onClick=${handleDownloadPdf}
                        >
                            Download PDF Report
                        </substrate-button>
                        <substrate-button
                            type="button"
                            onClick=${handleReset}
                        >
                            Reset
                        </substrate-button>
                    </div>
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
